import React, { useState, useCallback, useRef } from 'react';
import type { DiagnosticResult, StreamAnalysis, SegmentResult, HLSVariant, StreamTestPhase } from './types/diagnostics';
import { createEmptyResult } from './types/diagnostics';
import { NetworkTester } from './services/NetworkTester';
import { StreamTester } from './services/StreamTester';
import type { StreamProgress } from './services/StreamTester';
import { QualityScore } from './components/QualityScore';
import { RTTChart } from './components/RTTChart';
import { BandwidthChart } from './components/BandwidthChart';
import { ChunkWaterfall } from './components/ChunkWaterfall';
import { JitterHeatmap } from './components/JitterHeatmap';
import { FlowTimeline } from './components/FlowTimeline';
import { NetworkInfo } from './components/NetworkInfo';
import { SpeedGauge } from './components/SpeedGauge';
import { ProgressBar } from './components/ProgressBar';
import { StreamAnalysisView } from './components/StreamAnalysisView';

type TabType = 'stream' | 'network';

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('stream');

  // ---- Network Diagnostic State ----
  const [result, setResult] = useState<DiagnosticResult>(createEmptyResult());
  const testerRef = useRef<NetworkTester | null>(null);
  const isRunning = result.phase !== 'idle' && result.phase !== 'complete' && result.phase !== 'error';

  // ---- Stream Test State ----
  const [streamUrl, setStreamUrl] = useState('');
  const [streamPhase, setStreamPhase] = useState<StreamTestPhase>('idle');
  const [streamAnalysis, setStreamAnalysis] = useState<StreamAnalysis | null>(null);
  const [streamSegments, setStreamSegments] = useState<SegmentResult[]>([]);
  const [streamVariants, setStreamVariants] = useState<HLSVariant[]>([]);
  const [streamError, setStreamError] = useState<string | null>(null);
  const streamTesterRef = useRef<StreamTester | null>(null);
  const isStreamRunning = streamPhase !== 'idle' && streamPhase !== 'complete' && streamPhase !== 'error';

  // ---- Network Diagnostic Callbacks ----
  const handleProgress = useCallback((partial: Partial<DiagnosticResult>) => {
    setResult((prev) => ({ ...prev, ...partial }));
  }, []);

  const startTest = useCallback(async () => {
    setResult(createEmptyResult());
    const tester = new NetworkTester(handleProgress);
    testerRef.current = tester;
    const finalResult = await tester.runFullDiagnostic();
    setResult(finalResult);
    testerRef.current = null;
  }, [handleProgress]);

  const stopTest = useCallback(() => {
    testerRef.current?.abort();
    testerRef.current = null;
  }, []);

  const exportJSON = useCallback(() => {
    NetworkTester.downloadJSON(result);
  }, [result]);

  // ---- Stream Test Callbacks ----
  const handleStreamProgress = useCallback((progress: StreamProgress) => {
    setStreamPhase(progress.phase);
    setStreamAnalysis(progress.analysis);
    setStreamSegments(progress.segments);
    setStreamVariants(progress.variants);
    setStreamError(progress.error);
  }, []);

  const startStreamTest = useCallback(async () => {
    if (!streamUrl.trim()) return;
    setStreamPhase('connecting');
    setStreamAnalysis(null);
    setStreamSegments([]);
    setStreamVariants([]);
    setStreamError(null);

    const tester = new StreamTester(handleStreamProgress);
    streamTesterRef.current = tester;
    await tester.analyzeStream(streamUrl.trim());
    streamTesterRef.current = null;
  }, [streamUrl, handleStreamProgress]);

  const stopStreamTest = useCallback(() => {
    streamTesterRef.current?.abort();
    streamTesterRef.current = null;
  }, []);

  const exportStream = useCallback(() => {
    if (streamAnalysis) StreamTester.downloadReport(streamAnalysis);
  }, [streamAnalysis]);

  const shareLink = result.id ? `${window.location.origin}/api/results/${result.id}` : null;

  return (
    <div className="min-h-screen bg-dark-950 text-white">
      {/* Header */}
      <header className="border-b border-dark-700 bg-dark-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-sm font-bold">
              AV
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">AVCheck</h1>
              <p className="text-[10px] text-slate-500 -mt-0.5">Видеопоток & Сеть</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-dark-800 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab('stream')}
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${
                activeTab === 'stream'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Видеопоток
            </button>
            <button
              onClick={() => setActiveTab('network')}
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${
                activeTab === 'network'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Сеть
            </button>
          </div>

          <div className="flex items-center gap-2">
            {activeTab === 'network' && result.phase === 'complete' && (
              <>
                <button
                  onClick={exportJSON}
                  className="px-3 py-1.5 text-xs font-medium bg-dark-800 hover:bg-dark-700 rounded-lg border border-dark-700 transition-colors"
                >
                  Экспорт
                </button>
                {shareLink && (
                  <button
                    onClick={() => navigator.clipboard.writeText(shareLink)}
                    className="px-3 py-1.5 text-xs font-medium bg-dark-800 hover:bg-dark-700 rounded-lg border border-dark-700 transition-colors"
                  >
                    Ссылка
                  </button>
                )}
              </>
            )}
            {activeTab === 'stream' && streamPhase === 'complete' && streamAnalysis && (
              <button
                onClick={exportStream}
                className="px-3 py-1.5 text-xs font-medium bg-dark-800 hover:bg-dark-700 rounded-lg border border-dark-700 transition-colors"
              >
                Экспорт
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* ============================== STREAM TAB ============================== */}
        {activeTab === 'stream' && (
          <div className="space-y-6">
            {/* URL Input */}
            <div className="bg-dark-800 rounded-xl p-5 border border-dark-700">
              <h2 className="text-sm font-semibold text-slate-300 mb-3">Тестирование видеопотока</h2>
              <div className="flex gap-3">
                <input
                  type="url"
                  value={streamUrl}
                  onChange={(e) => setStreamUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !isStreamRunning && startStreamTest()}
                  placeholder="https://example.com/stream/playlist.m3u8"
                  disabled={isStreamRunning}
                  className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 disabled:opacity-50 transition-colors"
                />
                <button
                  onClick={isStreamRunning ? stopStreamTest : startStreamTest}
                  disabled={!streamUrl.trim() && !isStreamRunning}
                  className={`px-6 py-2.5 text-sm font-semibold rounded-lg transition-all whitespace-nowrap ${
                    isStreamRunning
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                      : 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white hover:from-blue-600 hover:to-cyan-500 shadow-lg shadow-blue-500/20 disabled:opacity-40 disabled:cursor-not-allowed'
                  }`}
                >
                  {isStreamRunning ? 'Остановить' : 'Проверить'}
                </button>
              </div>
              <p className="text-[11px] text-slate-600 mt-2">
                HLS (.m3u8), DASH (.mpd) или прямая ссылка на видео • Анализ сегментов, битрейта, буферизации
              </p>
            </div>

            {/* Stream Results */}
            <StreamAnalysisView
              phase={streamPhase}
              analysis={streamAnalysis}
              segments={streamSegments}
              variants={streamVariants}
              error={streamError}
            />

            {/* Idle state */}
            {streamPhase === 'idle' && !streamAnalysis && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-400/20 border border-purple-500/30 flex items-center justify-center mb-6">
                  <svg className="w-10 h-10 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold mb-2">Анализ видеопотока</h2>
                <p className="text-slate-400 text-sm max-w-md mb-4">
                  Вставьте ссылку на видеопоток (HLS/DASH/прямая ссылка) для проверки
                  доступности, скорости загрузки сегментов, буферизации и качества.
                </p>
                <div className="flex flex-wrap gap-2 justify-center text-xs text-slate-500">
                  <span className="bg-dark-800 px-2 py-1 rounded">HLS (.m3u8)</span>
                  <span className="bg-dark-800 px-2 py-1 rounded">DASH (.mpd)</span>
                  <span className="bg-dark-800 px-2 py-1 rounded">MP4/WebM</span>
                  <span className="bg-dark-800 px-2 py-1 rounded">Прямые ссылки</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================== NETWORK TAB ============================== */}
        {activeTab === 'network' && (
          <div className="space-y-6">
            {/* Start button */}
            <div className="flex justify-center">
              <button
                onClick={isRunning ? stopTest : startTest}
                className={`px-8 py-3 text-sm font-semibold rounded-xl transition-all ${
                  isRunning
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                    : 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white hover:from-blue-600 hover:to-cyan-500 shadow-lg shadow-blue-500/20'
                }`}
              >
                {isRunning ? 'Остановить' : result.phase === 'complete' ? 'Повторить' : 'Запустить диагностику'}
              </button>
            </div>

            {/* Progress */}
            {isRunning && (
              <div className="animate-fade-in">
                <ProgressBar phase={result.phase} progress={result.progress} />
              </div>
            )}

            {/* Error */}
            {result.error && result.phase === 'error' && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
                Ошибка: {result.error}
              </div>
            )}

            {/* Speed Gauges */}
            {(result.download || result.upload || isRunning) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                <SpeedGauge
                  label="Загрузка"
                  speed={result.download?.speedMbps ?? null}
                  direction="download"
                  isActive={result.phase === 'download'}
                />
                <SpeedGauge
                  label="Отдача"
                  speed={result.upload?.speedMbps ?? null}
                  direction="upload"
                  isActive={result.phase === 'upload'}
                />
              </div>
            )}

            {/* Quality Score */}
            {result.quality && (
              <div className="animate-fade-in">
                <QualityScore result={result} />
              </div>
            )}

            {/* Charts Grid */}
            {(result.ping || result.download || result.chunkAnalysis || result.streamDiagnostics) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {result.ping && result.ping.rtts.length > 0 && (
                  <div className="animate-fade-in">
                    <RTTChart ping={result.ping} />
                  </div>
                )}
                {result.ping && result.ping.rtts.length > 2 && (
                  <div className="animate-fade-in">
                    <JitterHeatmap ping={result.ping} />
                  </div>
                )}
                {(result.download || result.upload) && (
                  <div className="animate-fade-in">
                    <BandwidthChart download={result.download} upload={result.upload} />
                  </div>
                )}
                {result.chunkAnalysis && result.chunkAnalysis.chunks.length > 0 && (
                  <div className="animate-fade-in">
                    <ChunkWaterfall analysis={result.chunkAnalysis} />
                  </div>
                )}
                {result.streamDiagnostics && result.streamDiagnostics.flowPoints.length > 0 && (
                  <div className="animate-fade-in lg:col-span-2">
                    <FlowTimeline diagnostics={result.streamDiagnostics} />
                  </div>
                )}
              </div>
            )}

            {/* Network Info */}
            {(result.clientInfo || result.latencies.length > 0) && (
              <div className="animate-fade-in">
                <NetworkInfo
                  clientInfo={result.clientInfo}
                  latencies={result.latencies}
                  trace={result.trace}
                />
              </div>
            )}

            {/* Idle state */}
            {result.phase === 'idle' && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-400/20 border border-blue-500/30 flex items-center justify-center mb-6">
                  <svg className="w-10 h-10 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold mb-2">Диагностика сети</h2>
                <p className="text-slate-400 text-sm max-w-md mb-6">
                  Полный анализ интернет-соединения: пинг, скорость, джиттер,
                  потери пакетов и стабильность потока.
                </p>
                <p className="text-[11px] text-slate-600">
                  Занимает 30–60 секунд • Не требует установки ПО
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-dark-800 py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center text-xs text-slate-600">
          <span>AVCheck v1.0.0</span>
          <span>
            {activeTab === 'network' && result.phase === 'complete' && result.id && (
              <span>Result ID: {result.id.slice(0, 8)}</span>
            )}
          </span>
        </div>
      </footer>
    </div>
  );
};

export default App;
